/**
 * Quote guard - The Water Filter Factory
 *
 * Two jobs:
 *
 * 1. Drive the "Request a Quote" panel on quote-only product pages.
 *
 * 2. Close the wishlist bypass. Hiding the add-to-cart button does not stop an
 *    app such as GroPulse Wishlist, which POSTs straight to /cart/add.js. So
 *    instead of guarding the button, we watch for cart adds and then ask the
 *    server (via the quote-only-cart-check section) whether anything in the
 *    cart is quote-only. Anything that is gets removed again and the customer
 *    is shown a dialog pointing at the quote form.
 *
 *    This is the catch-at-the-door half. The server-rendered half lives in
 *    quote-only-cart-notice.liquid and the disabled checkout buttons, which
 *    still work with JavaScript switched off.
 */
(function () {
  'use strict';

  var TOAST_FLAG = 'wff:quoteOnlyRemoved';

  /* ------------------------------------------------------------------ *
   * Quote panel
   * ------------------------------------------------------------------ */

  function setupPanels() {
    document.querySelectorAll('[data-quote-request]').forEach(function (root) {
      var toggle = root.querySelector('[data-quote-toggle]');
      var panel = root.querySelector('[data-quote-panel]');
      if (!toggle || !panel || toggle.dataset.quoteBound === 'true') return;

      toggle.dataset.quoteBound = 'true';
      toggle.addEventListener('click', function () {
        var isOpen = panel.hidden === false;
        setPanel(root, !isOpen);
        if (!isOpen) {
          var firstField = panel.querySelector('input:not([type="hidden"]), select, textarea');
          if (firstField) firstField.focus();
        }
      });

      // Re-open after a submit, or when linked to from the cart notice.
      var justPosted = window.location.search.indexOf('contact_posted=true') !== -1;
      var linkedTo = window.location.hash === '#quote';
      if (justPosted || linkedTo || panel.querySelector('[data-quote-success]')) {
        setPanel(root, true);
        if (justPosted) {
          var success = panel.querySelector('[data-quote-success]');
          if (success) {
            success.scrollIntoView({ behavior: 'smooth', block: 'center' });
            success.focus();
          }
        }
      }
    });
  }

  function setPanel(root, open) {
    var toggle = root.querySelector('[data-quote-toggle]');
    var panel = root.querySelector('[data-quote-panel]');
    var label = root.querySelector('[data-quote-toggle-label]');
    if (!toggle || !panel) return;

    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (label) label.textContent = open ? 'Hide quote form' : 'Request a Quote';
  }

  /* ------------------------------------------------------------------ *
   * Line item: live subtotal, and the value the email carries
   * ------------------------------------------------------------------ */

  // Matches the store's ZAR display, e.g. "R 1 250.00".
  function money(cents) {
    var value = (cents / 100).toFixed(2).split(".");
    var whole = value[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return "R " + whole + "." + value[1];
  }

  function setupLineItems() {
    document.querySelectorAll("[data-quote-request]").forEach(function (root) {
      var qty = root.querySelector("[data-quote-qty]");
      var priceEl = root.querySelector("[data-unit-cents]");
      if (!qty || !priceEl || qty.dataset.qtyBound === "true") return;

      qty.dataset.qtyBound = "true";
      var unitCents = parseInt(priceEl.dataset.unitCents, 10);
      if (isNaN(unitCents)) return;

      var subtotalEl = root.querySelector("[data-quote-subtotal]");
      var totalEl = root.querySelector("[data-quote-total]");
      var hiddenField = root.querySelector("[data-quote-subtotal-field]");
      var readbackEl = root.querySelector("[data-quote-readback]");
      var requestField = root.querySelector("[data-quote-request-field]");
      var productName = root.dataset.quoteProduct || "";

      function update() {
        var n = parseInt(qty.value, 10);
        if (!n || n < 1) n = 1;
        var total = unitCents * n;
        var formatted = money(total);
        if (subtotalEl) subtotalEl.textContent = formatted;
        if (totalEl) totalEl.textContent = formatted;
        if (hiddenField) hiddenField.value = formatted;

        // The customer reads this, and the identical sentence is emailed to the
        // shop, so a misread quantity is visible to both sides before sending.
        if (readbackEl) readbackEl.textContent = n + " × " + productName;
        if (requestField) requestField.value = n + " x " + productName;
      }

      qty.addEventListener("input", update);
      qty.addEventListener("change", update);
      update();
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart guard
   * ------------------------------------------------------------------ */

  var checkScheduled = false;

  function scheduleCartCheck() {
    if (checkScheduled) return;
    checkScheduled = true;
    // Just enough for the app that made the add to finish its own cart refresh.
    // Kept short so the message lands close to the click.
    window.setTimeout(function () {
      checkScheduled = false;
      runCartCheck();
    }, 150);
  }

  function runCartCheck() {
    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + '?section_id=quote-only-cart-check'
      : '/?section_id=quote-only-cart-check')
      .then(function (res) {
        if (!res.ok) throw new Error('cart check failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-quote-only-cart]');
        if (!node) return null;

        var payload;
        try {
          payload = JSON.parse(node.textContent);
        } catch (err) {
          return null;
        }
        if (!payload || !payload.lines || !payload.lines.length) return null;
        return removeLines(payload.lines);
      })
      .catch(function (err) {
        // Never let the guard break the storefront. The server-rendered notice
        // and the disabled checkout button still hold the line.
        if (window.console) console.warn('[quote-guard]', err);
      });
  }

  function removeLines(lines) {
    var chain = Promise.resolve();

    lines.forEach(function (line) {
      chain = chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 })
        });
      });
    });

    return chain.then(function () {
      var first = lines[0];
      var payload = lines.length === 1
        ? {
            title: 'This item needs a quote',
            body: first.title + ' cannot be bought online. It ships as freight, so we quote '
              + 'delivery for your address first. We have taken it back out of your cart.',
            url: first.url
          }
        : {
            title: 'These items need a quote',
            body: lines.length + ' items in your cart ship as freight and need a delivery quote, '
              + 'so we have taken them back out of your cart.',
            url: first.url
          };

      try {
        sessionStorage.setItem(TOAST_FLAG, JSON.stringify(payload));
      } catch (err) {
        /* private browsing - fall through to the immediate message */
      }

      showBlockedMessage(payload);
      refreshCartUi();
    });
  }

  function refreshCartUi() {
    // Keep the header count honest.
    fetch('/?section_id=cart-icon-bubble')
      .then(function (res) { return res.ok ? res.text() : null; })
      .then(function (html) {
        if (!html) return;
        var target = document.getElementById('cart-icon-bubble');
        if (!target) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById('cart-icon-bubble') || doc.querySelector('.shopify-section');
        if (fresh) target.innerHTML = fresh.innerHTML;
      })
      .catch(function () { /* cosmetic only */ });

    // On the cart page the whole total needs recalculating, so reload. The
    // message is replayed from sessionStorage after the reload.
    if (window.location.pathname.indexOf('/cart') === 0) {
      window.setTimeout(function () { window.location.reload(); }, 1200);
    }
  }

  /* ------------------------------------------------------------------ *
   * Blocked message
   *
   * A centred dialog rather than a corner toast. The wishlist's own "added to
   * cart" confirmation fires first, and a toast at the bottom of the screen
   * competes with the chat bubble and Shopify's preview bar, so it gets missed.
   * This has to be dismissed, which is the point.
   * ------------------------------------------------------------------ */

  function showBlockedMessage(data) {
    var existing = document.querySelector('.quote-only-modal');
    if (existing) existing.remove();

    var overlay = document.createElement('div');
    overlay.className = 'quote-only-modal';

    var dialog = document.createElement('div');
    dialog.className = 'quote-only-modal__dialog';
    dialog.setAttribute('role', 'alertdialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'QuoteOnlyModalTitle');
    dialog.tabIndex = -1;

    var heading = document.createElement('h2');
    heading.className = 'quote-only-modal__title';
    heading.id = 'QuoteOnlyModalTitle';
    heading.textContent = data.title;
    dialog.appendChild(heading);

    var body = document.createElement('p');
    body.className = 'quote-only-modal__body';
    body.textContent = data.body;
    dialog.appendChild(body);

    var actions = document.createElement('div');
    actions.className = 'quote-only-modal__actions';

    if (data.url) {
      var cta = document.createElement('a');
      cta.className = 'button quote-only-modal__cta';
      cta.href = data.url + '#quote';
      cta.textContent = 'Request a quote';
      actions.appendChild(cta);
    }

    var dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'quote-only-modal__dismiss';
    dismiss.textContent = 'Keep shopping';
    actions.appendChild(dismiss);

    dialog.appendChild(actions);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
    dialog.focus();

    function close() {
      overlay.remove();
      document.removeEventListener('keydown', onKeydown);
    }

    function onKeydown(event) {
      if (event.key === 'Escape') close();
    }

    dismiss.addEventListener('click', close);
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) close();
    });
    document.addEventListener('keydown', onKeydown);
  }

  function replayBlockedMessage() {
    var raw;
    try {
      raw = sessionStorage.getItem(TOAST_FLAG);
      sessionStorage.removeItem(TOAST_FLAG);
    } catch (err) {
      return;
    }
    if (!raw) return;
    try {
      showBlockedMessage(JSON.parse(raw));
    } catch (err) {
      /* ignore malformed flag */
    }
  }

  /* ------------------------------------------------------------------ *
   * Watch for cart adds, whoever makes them
   * ------------------------------------------------------------------ */

  function isCartAdd(url) {
    return typeof url === 'string' && url.indexOf('/cart/add') !== -1;
  }

  function interceptFetch() {
    if (!window.fetch || window.fetch.__quoteGuard) return;

    var original = window.fetch;
    var patched = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var result = original.apply(this, arguments);
      if (isCartAdd(url)) {
        result.then(function () { scheduleCartCheck(); }).catch(function () {});
      }
      return result;
    };
    patched.__quoteGuard = true;
    window.fetch = patched;
  }

  function interceptXhr() {
    var proto = XMLHttpRequest.prototype;
    if (proto.open.__quoteGuard) return;

    var originalOpen = proto.open;
    var patchedOpen = function (method, url) {
      this.__quoteGuardUrl = url;
      return originalOpen.apply(this, arguments);
    };
    patchedOpen.__quoteGuard = true;
    proto.open = patchedOpen;

    var originalSend = proto.send;
    proto.send = function () {
      var self = this;
      if (isCartAdd(self.__quoteGuardUrl)) {
        self.addEventListener('load', function () { scheduleCartCheck(); });
      }
      return originalSend.apply(this, arguments);
    };
  }

  /* ------------------------------------------------------------------ *
   * Belt and braces: keep checkout disabled while the notice is present
   * ------------------------------------------------------------------ */

  function lockCheckout() {
    if (!document.querySelector('[data-quote-only-notice]')) return;
    document.querySelectorAll('[name="checkout"]').forEach(function (button) {
      button.setAttribute('disabled', 'disabled');
    });
    document.querySelectorAll('.cart__dynamic-checkout-buttons, .additional-checkout-buttons').forEach(
      function (node) { node.setAttribute('hidden', 'hidden'); }
    );
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  function init() {
    setupPanels();
    setupLineItems();
    lockCheckout();
    replayBlockedMessage();
  }

  interceptFetch();
  interceptXhr();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Cart drawers and variant changes re-render chunks of the page, so re-bind.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('cart:refresh', init);
  document.addEventListener('cart-drawer:open', init);
})();
